/**
 * Vesta LOS API v26.1 — Enumerations
 * Aligned with Vesta MISMO 3.4 / URLA models.
 * Source: https://uff.beta.vesta.com/api?version=_26_1
 */

// ─── Borrower ────────────────────────────────────────────────

// Vesta: MaritalStatus
export const MARITAL_STATUS_TYPES = [
  { value: 'Married', label: 'Married' },
  { value: 'Separated', label: 'Separated' },
  { value: 'Unmarried', label: 'Unmarried (Single, Divorced, Widowed)' },
];

// Vesta: CitizenshipType
export const CITIZENSHIP_TYPES = [
  { value: 'USCitizen', label: 'U.S. Citizen' },
  { value: 'PermanentResidentAlien', label: 'Permanent Resident Alien' },
  { value: 'NonPermanentResidentAlien', label: 'Non-Permanent Resident Alien' },
  { value: 'NonResidentAlien', label: 'Non-Resident Alien' },
];

// Vesta: DomesticRelationshipStatus
export const DOMESTIC_RELATIONSHIP_STATUS_TYPES = [
  { value: 'CivilUnion', label: 'Civil Union' },
  { value: 'DomesticPartnership', label: 'Domestic Partnership' },
  { value: 'RegisteredReciprocalBeneficiaryRelationship', label: 'Registered Reciprocal Beneficiary' },
  { value: 'Other', label: 'Other' },
];

// Vesta: MilitaryStatusType
export const MILITARY_STATUS_TYPES = [
  { value: 'None', label: 'None' },
  { value: 'ActiveDuty', label: 'Active Duty' },
  { value: 'ReserveNationalGuardNeverActivated', label: 'Reserve / National Guard (Never Activated)' },
  { value: 'Veteran', label: 'Veteran' },
];

// Vesta: MilitaryDischargeType
export const MILITARY_DISCHARGE_TYPES = [
  { value: 'Honorable', label: 'Honorable' },
  { value: 'Dishonorable', label: 'Dishonorable' },
  { value: 'Other', label: 'Other' },
];

// Vesta: LanguagePreferenceType
export const LANGUAGE_PREFERENCE_TYPES = [
  { value: 'English', label: 'English' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Tagalog', label: 'Tagalog' },
  { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Other', label: 'Other' },
  { value: 'RefuseToAnswer', label: 'Prefer not to answer' },
];

// Vesta: PreferredContactMethod
export const PREFERRED_CONTACT_METHODS = [
  { value: 'Email', label: 'Email' },
  { value: 'MobilePhone', label: 'Mobile Phone' },
  { value: 'HomePhone', label: 'Home Phone' },
  { value: 'WorkPhone', label: 'Work Phone' },
  { value: 'SMS', label: 'SMS / Text' },
];

// Vesta: ApplicationSubmitMethod
export const APPLICATION_SUBMIT_METHODS = [
  { value: 'Email', label: 'Email' },
  { value: 'FaceToFace', label: 'Face to Face' },
  { value: 'Fax', label: 'Fax' },
  { value: 'Internet', label: 'Internet' },
  { value: 'Mail', label: 'Mail' },
  { value: 'Telephone', label: 'Telephone' },
];

// Vesta: TaxIdentifierType
export const TAX_IDENTIFIER_TYPES = [
  { value: 'SocialSecurityNumber', label: 'Social Security Number (SSN)' },
  { value: 'IndividualTaxpayerIdentificationNumber', label: 'Individual Taxpayer ID (ITIN)' },
];

// ─── Employment ──────────────────────────────────────────────

// Vesta: EmploymentClassificationType
export const EMPLOYMENT_CLASSIFICATION_TYPES = [
  { value: 'Primary', label: 'Primary Employment' },
  { value: 'Secondary', label: 'Secondary / Part-Time Employment' },
];

// Vesta: EmployerStatus
export const EMPLOYER_STATUS_TYPES = [
  { value: 'Current', label: 'Current' },
  { value: 'Previous', label: 'Previous' },
];

// Vesta: SpecialBorrowerEmployerRelationshipType
export const SPECIAL_EMPLOYER_RELATIONSHIP_TYPES = [
  { value: 'EmployedByRelative', label: 'Employed by Relative' },
  { value: 'PropertySeller', label: 'Property Seller' },
  { value: 'RealEstateBroker', label: 'Real Estate Broker' },
  { value: 'Other', label: 'Other' },
];

// Ownership share (for self-employed borrowers)
export const OWNERSHIP_SHARE_TYPES = [
  { value: 'LessThan25Percent', label: 'Less than 25%' },
  { value: '25OrMore', label: '25% or more' },
];

// ─── Income ──────────────────────────────────────────────────

// Vesta: IncomeType — exact enum values from API v26.1
export const INCOME_TYPES = [
  { value: 'Employment', label: 'Employment', category: 'employment' },
  { value: 'MilitaryEmployment', label: 'Military Employment', category: 'employment' },
  { value: 'SelfEmployment', label: 'Self-Employment', category: 'employment' },
  { value: 'IndependentContractor', label: 'Independent Contractor', category: 'employment' },
  { value: 'Rental', label: 'Rental Income', category: 'other' },
  { value: 'AccessoryUnit', label: 'Accessory Unit', category: 'other' },
  { value: 'Boarder', label: 'Boarder Income', category: 'other' },
  { value: 'SocialSecurity', label: 'Social Security', category: 'other' },
  { value: 'Pension', label: 'Pension', category: 'other' },
  { value: 'CapitalGains', label: 'Capital Gains', category: 'other' },
  { value: 'DividendsInterest', label: 'Dividends / Interest', category: 'other' },
  { value: 'Retirement', label: 'Retirement', category: 'other' },
  { value: 'DeferredCompensation', label: 'Deferred Compensation', category: 'other' },
  { value: 'AutomobileAllowance', label: 'Automobile Allowance', category: 'other' },
  { value: 'HousingAllowance', label: 'Housing Allowance', category: 'other' },
  { value: 'Disability', label: 'Disability', category: 'other' },
  { value: 'PublicAssistance', label: 'Public Assistance', category: 'other' },
  { value: 'UnemploymentBenefits', label: 'Unemployment Benefits', category: 'other' },
  { value: 'VABenefitsNonEducational', label: 'VA Benefits (Non-Educational)', category: 'other' },
  { value: 'Alimony', label: 'Alimony', category: 'other' },
  { value: 'ChildSupport', label: 'Child Support', category: 'other' },
  { value: 'SeparateMaintenance', label: 'Separate Maintenance', category: 'other' },
  { value: 'FosterCare', label: 'Foster Care', category: 'other' },
  { value: 'HousingChoiceVoucherProgram', label: 'Housing Choice Voucher (Section 8)', category: 'other' },
  { value: 'MortgageCreditCertificate', label: 'Mortgage Credit Certificate', category: 'other' },
  { value: 'MortgageDifferential', label: 'Mortgage Differential', category: 'other' },
  { value: 'NotesReceivableInstallment', label: 'Notes Receivable (Installment)', category: 'other' },
  { value: 'Royalties', label: 'Royalties', category: 'other' },
  { value: 'TemporaryLeave', label: 'Temporary Leave', category: 'other' },
  { value: 'Trust', label: 'Trust', category: 'other' },
  { value: 'AssetBasedIncome', label: 'Asset-Based Income', category: 'other' },
  { value: 'Other', label: 'Other', category: 'other' },
];

// ─── Address ─────────────────────────────────────────────────

// Vesta: AddressType
export const ADDRESS_TYPES = [
  { value: 'Residential', label: 'Residential' },
  { value: 'Mailing', label: 'Mailing' },
  { value: 'Other', label: 'Other' },
];

// Vesta: Address.occupancyType (housing expense basis)
export const RESIDENCY_BASIS_TYPES = [
  { value: 'Own', label: 'Own' },
  { value: 'Rent', label: 'Rent' },
  { value: 'LivingRentFree', label: 'Living Rent Free' },
];

// Vesta: StateCode
export const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  // Territories
  { value: 'AS', label: 'American Samoa' },
  { value: 'GU', label: 'Guam' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'VI', label: 'U.S. Virgin Islands' },
  // Military
  { value: 'AA', label: 'Armed Forces Americas' },
  { value: 'AE', label: 'Armed Forces Europe' },
  { value: 'AP', label: 'Armed Forces Pacific' },
];

// Vesta: CountryType (common subset)
export const COUNTRY_TYPES = [
  { value: 'US', label: 'United States' },
  { value: 'Other', label: 'Other' },
];

// Vesta: AddressUnitDesignatorType (not explicitly enumerated — kept from MISMO standard)
export const UNIT_TYPES = [
  { value: '', label: '(none)' },
  { value: 'Apartment', label: 'Apt' },
  { value: 'Suite', label: 'Suite' },
  { value: 'Unit', label: 'Unit' },
  { value: 'Room', label: 'Room' },
  { value: 'Floor', label: 'Floor' },
];

// ─── Phone ───────────────────────────────────────────────────

// Vesta: PhoneNumberType
export const PHONE_TYPES = [
  { value: 'Mobile', label: 'Mobile' },
  { value: 'Home', label: 'Home' },
  { value: 'Work', label: 'Work' },
  { value: 'Other', label: 'Other' },
];

// ─── Name Suffix ─────────────────────────────────────────────

export const NAME_SUFFIXES = [
  { value: '', label: '(none)' },
  { value: 'Jr', label: 'Jr.' },
  { value: 'Sr', label: 'Sr.' },
  { value: 'II', label: 'II' },
  { value: 'III', label: 'III' },
  { value: 'IV', label: 'IV' },
];

// ─── Loan ────────────────────────────────────────────────────

// Vesta: LoanPurpose
export const LOAN_PURPOSE_TYPES = [
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Refinance', label: 'Refinance' },
];

// Vesta: MortgageType
export const MORTGAGE_TYPES = [
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'VA', label: 'VA' },
  { value: 'USDA', label: 'USDA' },
];

// Vesta: LoanType
export const LOAN_TYPES = [
  { value: 'Mortgage', label: 'Mortgage' },
  { value: 'HELOC', label: 'HELOC' },
  { value: 'HomeEquityLoan', label: 'Home Equity Loan' },
];

// Vesta: LienPriorityType
export const LIEN_PRIORITY_TYPES = [
  { value: 'FirstLien', label: 'First Lien' },
  { value: 'SecondLien', label: 'Second Lien' },
  { value: 'ThirdLien', label: 'Third Lien' },
  { value: 'FourthLien', label: 'Fourth Lien' },
];

// ─── Property ────────────────────────────────────────────────

// Vesta: PropertyType
export const PROPERTY_TYPES = [
  { value: 'SingleFamily', label: 'Single Family' },
  { value: 'Condominium', label: 'Condominium' },
  { value: 'Cooperative', label: 'Cooperative' },
  { value: 'TwoToFourUnitProperty', label: '2-4 Unit Property' },
  { value: 'ManufacturedHome', label: 'Manufactured Home' },
  { value: 'PlannedUnitDevelopment', label: 'Planned Unit Development (PUD)' },
  { value: 'Land', label: 'Land' },
  { value: 'MultiFamily', label: 'Multi-Family' },
  { value: 'Condotel', label: 'Condotel' },
];

// Vesta: PropertyUsageType (occupancy)
export const PROPERTY_USAGE_TYPES = [
  { value: 'PrimaryResidence', label: 'Primary Residence' },
  { value: 'SecondHome', label: 'Second Home' },
  { value: 'Investment', label: 'Investment Property' },
  { value: 'Other', label: 'Other' },
];

// Vesta: ProjectDesignType
export const PROJECT_DESIGN_TYPES = [
  { value: 'Garden', label: 'Garden' },
  { value: 'Highrise', label: 'High-Rise' },
  { value: 'Midrise', label: 'Mid-Rise' },
  { value: 'TownhouseRowhouse', label: 'Townhouse / Rowhouse' },
  { value: 'Other', label: 'Other' },
];

// Vesta: ConstructionMethodType
export const CONSTRUCTION_METHOD_TYPES = [
  { value: 'SiteBuilt', label: 'Site Built' },
  { value: 'Manufactured', label: 'Manufactured' },
  { value: 'MHAdvantage', label: 'MH Advantage' },
  { value: 'Modular', label: 'Modular' },
  { value: 'OnFrameModular', label: 'On-Frame Modular' },
  { value: 'MobileHome', label: 'Mobile Home' },
  { value: 'Container', label: 'Container' },
  { value: 'ThreeDimensionalPrintingTechnology', label: '3D Printing Technology' },
  { value: 'Other', label: 'Other' },
];

// ─── Credit ──────────────────────────────────────────────────

// Vesta: CreditPullType
export const CREDIT_PULL_TYPES = [
  { value: 'Hard', label: 'Hard Pull' },
  { value: 'Soft', label: 'Soft Pull' },
];

// Vesta: CreditPullReportType
export const CREDIT_PULL_REPORT_TYPES = [
  { value: 'Merge', label: 'Merge' },
  { value: 'MergePlus', label: 'Merge Plus' },
  { value: 'NonTraditional', label: 'Non-Traditional' },
  { value: 'RiskScoresOnly', label: 'Risk Scores Only' },
  { value: 'RMCR', label: 'RMCR' },
  { value: 'RMCRForeign', label: 'RMCR Foreign' },
  { value: 'Other', label: 'Other' },
];

// Vesta: EconsentStatus
export const ECONSENT_STATUS_TYPES = [
  { value: 'Accept', label: 'Accepted' },
  { value: 'Decline', label: 'Declined' },
  { value: 'Withdrawn', label: 'Withdrawn' },
];

// Vesta: EconsentCaptureMethod
export const ECONSENT_CAPTURE_METHODS = [
  { value: 'Electronic', label: 'Electronic' },
  { value: 'Verbal', label: 'Verbal' },
];
